/** ICP supervisor oxide ↔ element reporting (v1: 10 majors + S/SO₃). Factors from lab Excel. */
export interface IcpOxideConversionPair {
  element: string;
  oxide: string;
  factor: number;
}

export const ICP_REPORT_FORM_V1: IcpOxideConversionPair[] = [
  { element: 'Al', oxide: 'Al2O3', factor: 1.8895 },
  { element: 'Ca', oxide: 'CaO', factor: 1.3992 },
  { element: 'Fe', oxide: 'Fe2O3', factor: 1.4297 },
  { element: 'K', oxide: 'K2O', factor: 1.2046 },
  { element: 'Mg', oxide: 'MgO', factor: 1.6582 },
  { element: 'Mn', oxide: 'MnO', factor: 1.2912 },
  { element: 'Na', oxide: 'Na2O', factor: 1.348 },
  { element: 'P', oxide: 'P2O5', factor: 2.2916 },
  { element: 'Si', oxide: 'SiO2', factor: 2.1392 },
  { element: 'Ti', oxide: 'TiO2', factor: 1.6681 },
  { element: 'S', oxide: 'SO3', factor: 2.4972 },
];

export type SupervisorReportFormEntry = {
  reportAs: string;
  sourceSymbol: string;
  oxide: string;
  element: string;
  factor: number;
  mode: 'oxide' | 'element';
};

export type SupervisorReportFormMap = Record<string, SupervisorReportFormEntry>;

const BY_ELEMENT = new Map(ICP_REPORT_FORM_V1.map(p => [p.element, p]));
const BY_OXIDE = new Map(ICP_REPORT_FORM_V1.map(p => [p.oxide, p]));
const BY_SYMBOL = new Map<string, IcpOxideConversionPair>();
for (const p of ICP_REPORT_FORM_V1) {
  BY_SYMBOL.set(p.element, p);
  BY_SYMBOL.set(p.oxide, p);
}

export function getIcpConversionPair(symbol: string): IcpOxideConversionPair | null {
  return BY_SYMBOL.get(symbol) || null;
}

export function isReportFormConvertible(symbol: string): boolean {
  return BY_SYMBOL.has(symbol);
}

/** Stored ppm-scale major reading → display % (oxide form). */
export function icpStoredPpmToOxidePercent(storedPpm: number): number {
  if (!storedPpm || !isFinite(storedPpm)) return 0;
  return Math.abs(storedPpm) >= 100 ? storedPpm / 10000 : storedPpm;
}

/** Convert stored ppm (oxide-keyed reading) to element % for certificate/matrix display. */
export function icpStoredPpmToElementPercent(storedPpm: number, factor: number): number {
  const oxidePct = icpStoredPpmToOxidePercent(storedPpm);
  return factor > 0 ? oxidePct / factor : oxidePct;
}

export function resolveReportFormColumn(
  columnSymbol: string,
  reportForm: SupervisorReportFormMap,
): { displaySymbol: string; dataKey: string; entry: SupervisorReportFormEntry | null } {
  const direct = reportForm[columnSymbol];
  if (direct) {
    return { displaySymbol: direct.reportAs, dataKey: direct.sourceSymbol, entry: direct };
  }
  for (const entry of Object.values(reportForm)) {
    if (entry.reportAs === columnSymbol) {
      return { displaySymbol: entry.reportAs, dataKey: entry.sourceSymbol, entry };
    }
  }
  return { displaySymbol: columnSymbol, dataKey: columnSymbol, entry: null };
}

export function buildReportFormEntry(
  columnSymbol: string,
  mode: 'oxide' | 'element',
  lotFinalElements: string[],
): SupervisorReportFormEntry | null {
  const pair = getIcpConversionPair(columnSymbol);
  if (!pair) return null;
  if (mode === 'oxide') return null;
  const hasOxide = lotFinalElements.includes(pair.oxide);
  const hasElement = lotFinalElements.includes(pair.element);
  let sourceSymbol = columnSymbol;
  if (hasOxide && !hasElement) sourceSymbol = pair.oxide;
  else if (hasElement && !hasOxide) sourceSymbol = pair.element;
  else if (hasOxide) sourceSymbol = pair.oxide;
  else sourceSymbol = pair.element;
  return {
    reportAs: pair.element,
    sourceSymbol,
    oxide: pair.oxide,
    element: pair.element,
    factor: pair.factor,
    mode: 'element',
  };
}

export function shouldIncludeSupervisorEditInCert(
  elementKey: string,
  activeCertElements: string[] | null,
  reportForm: SupervisorReportFormMap,
): boolean {
  if (elementKey === '__PAF__') return false;
  if (!activeCertElements) return true;
  if (activeCertElements.includes(elementKey)) return true;
  for (const entry of Object.values(reportForm)) {
    if (entry.sourceSymbol === elementKey && activeCertElements.includes(entry.reportAs)) return true;
  }
  return false;
}

